import createBreakpoints from "@material-ui/core/styles/createBreakpoints";
import createPalette from "@material-ui/core/styles/createPalette";
import createTypography from "@material-ui/core/styles/createTypography";
import { Overrides } from "@material-ui/core/styles/overrides";
import { Skin } from "themes/model";
import { createCustomTheme } from "./createCustomTheme";

const logoPath = "https://ringconfigimg.blob.core.windows.net/skins/ChristiaanPicard/logo.png";

const headerPath = "https://ringconfigimg.blob.core.windows.net/skins/ChristiaanPicard/bg.jpg";

export const palette = createPalette({
  common: {
    white: "#ffffff",
    black: "#61605a",
  },
  primary: {
    light: "#ccc28a",
    main: "#c4a36c",
  },
  text: {
    secondary: "#666666",
    primary: "#1F1F1F",
  },
  error: {
    light: "#FF5833",
    main: "#E43A15",
    dark: "#D02601",
  },
  warning: {
    light: "#FF893E",
    main: "#EF6B20",
    dark: "#DB570C",
  },
  info: {
    light: "#1E97FD",
    main: "#0079DF",
    dark: "#0065CB",
  },
  success: {
    light: "#1EC669",
    main: "#00A84B",
    dark: "#009437",
  },
  background: {
    default: "#ffffff",
  },
  grey: {
    100: "#F7F7F7",
    200: "#EEEEEE",
    300: "#E7E7E7",
    400: "#E4E2E3",
    500: "#B2B2B2",
    600: "#8C8C8C",
    700: "#666666",
    800: "#333333",
    900: "#1F1F1F",
  },
  divider: "#ccc28a",
});

const fontFamilyHeader = "Prata";
const fontFamily = "Roboto";

export const typography = createTypography(palette, {
  h1: {
    fontStyle: "normal",
    fontWeight: 900,
    fontFamily: fontFamilyHeader,
    fontSize: 33,
    lineHeight: 1,
    letterSpacing: 0,
  },
  h2: {
    fontStyle: "normal",
    fontWeight: 900,
    fontFamily: fontFamilyHeader,
    fontSize: "26px",
    lineHeight: 1,
    letterSpacing: 0,
  },
  h3: {
    fontStyle: "normal",
    fontWeight: 400,
    fontFamily: fontFamilyHeader,
    fontSize: "20px",
    lineHeight: 1,
    letterSpacing: 0,
  },
  h4: {
    fontFamily: fontFamilyHeader,
    fontStyle: "normal",
    fontSize: "18px",
    lineHeight: 1,
    fontWeight: 400,
    letterSpacing: 0,
  },
  h5: {
    fontFamily: fontFamilyHeader,
    fontStyle: "normal",
    fontSize: "16px",
    lineHeight: 1.2,
    fontWeight: 700,
    letterSpacing: 0,
  },
  h6: {
    fontFamily: fontFamilyHeader,
    fontStyle: "normal",
    fontSize: "16px",
    fontWeight: 900,
    lineHeight: 1,
    letterSpacing: 0,
  },
  subtitle1: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "12px",
    lineHeight: 1.2,
    fontWeight: 400,
    letterSpacing: 0,
  },
  subtitle2: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "22px",
    lineHeight: 1.2,
    fontWeight: 300,
    letterSpacing: 0,
    textTransform: "uppercase" as "uppercase",
  },
  button: {
    fontFamily,
    fontStyle: "normal",
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: 1,
    letterSpacing: "1px",
  },
  caption: {
    fontFamily,
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: 1.4,
    letterSpacing: 0,
  },
  body1: {
    fontFamily,
    fontSize: "16px",
    fontWeight: 300,
    lineHeight: 1.4,
    letterSpacing: 0,
  },
  body2: {
    fontFamily,
    fontSize: "14px",
    fontWeight: 300,
    lineHeight: 1.4,
    letterSpacing: 0,
    "-webkit-font-smoothing": "subpixel-antialiased",
  },
});

const shape = {
  borderRadius: 20,
};

const breakpoints = createBreakpoints({
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1206,
    xl: 1920,
  },
});

const overrides: Overrides = {
  MuiCssBaseline: {
    "@global": {
      "@font-face": [
		{ /* Roboto - Latin */
		  fontFamily: "Roboto",
		  fontDisplay: "swap",
		  fontStyle: "normal",
		  fontWeight: 300,
		  src: "url(https://fonts.gstatic.com/s/roboto/v48/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWuaabVmUiAo.woff2) format('woff2')",
		  unicodeRange: "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
		},
		{ /* Roboto - Latin */
		  fontFamily: "Roboto",
		  fontDisplay: "swap",
		  fontStyle: "normal",
		  fontWeight: 700,
		  src: "url(https://fonts.gstatic.com/s/roboto/v48/KFO7CnqEu92Fr1ME7kSn66aGLdTylUAMa3yUBA.woff2) format('woff2')",
		  unicodeRange: "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
		},
		{ /* Roboto - Latin extended */
		  fontFamily: "Roboto",
		  fontDisplay: "swap",
		  fontStyle: "normal",
		  fontWeight: 300,
		  src: "url(https://fonts.gstatic.com/s/roboto/v48/KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWuaabVmaiArmlw.woff2) format('woff2')",
		  unicodeRange: "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF",
		},
		{ /* Roboto - Latin extended */
		  fontFamily: "Roboto",
		  fontDisplay: "swap",
		  fontStyle: "normal",
		  fontWeight: 700,
		  src: "url(https://fonts.gstatic.com/s/roboto/v48/KFO7CnqEu92Fr1ME7kSn66aGLdTylUAMa3KUBGEe.woff2) format('woff2')",
		  unicodeRange: "U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF",
		},
        { /* Prata - Latin */
		  fontFamily: "Prata",
          fontDisplay: "swap",
		  fontStyle: "normal",
		  fontWeight: 400,
		  src: "url(https://fonts.gstatic.com/s/prata/v21/6xKhdSpbNNCT-sWPCm4.woff2) format('woff2')",
		  unicodeRange: "U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD",
		},
      ],
      ".value-icon":
      {
        height: ".75em"
      },
	  "div[class^='makeStyles-filter-']":
	  {
		color: "#ffffff",
		fontWeight: 400
	  },
	  "div[class^='makeStyles-thumbnail-']": 
	  {
		borderColor: "#ccc28a"
	  },
	  "legend.MuiFormLabel-root, .MuiSvgIcon-root, .MuiSvgIcon-root, label.MuiFormLabel-root, h4":
	  {
		color: "#ffffff",
		textTransform: "uppercase"
	  },
	  ".MuiButton-root":
	  {
		fontFamily: fontFamilyHeader,
		fontSize: 14
	  },
	  ".MuiTypography-colorTextSecondary, .MuiListItemIcon-root > svg":
	  {
		color: "#61605a"
	  },
	  ".MuiChip-colorPrimary, .MuiButton-containedPrimary":
	  {
		backgroundColor: "#ffffff",
		color: "#ccc28a"
	  },
	  ".MuiChip-colorPrimary:hover, .MuiButton-containedPrimary:hover":
	  {
		backgroundColor: "#ccc28a",
		color: "#ffffff"
	  },
	  ".MuiChip-deleteIconColorPrimary, .MuiListItemText-primary":
	  {
		fontFamily: fontFamilyHeader,
		color: "#ccc28a",
		fontSize: 12
	  }/*,
	  ".MuiToolbar-regular":
	  {
		minHeight: "136px !important"
	  },
	  "img[class^='makeStyles-logo-']":
	  {
		maxHeight: 62
	  }*/
    },
  },
  MuiAppBar: {
    colorDefault: { 
      backgroundColor: "#ffffff"
    },
  },
  MuiButton: {
    outlinedPrimary: { 
      color: "#343C48",
      borderColor: "#343C48",
    },
    containedPrimary: {
      backgroundColor: "#ccc28a",
      borderColor: "#343C48",
    },
  }
};

export const skin: Skin = {
  logoPath,
  headerPath,
  headerHeight: 350,
  theme: createCustomTheme(palette, typography, shape, breakpoints, overrides),
};
